import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './authConfig';
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext';
import MainLayout from './components/Layout/MainLayout';
import Home from './pages/Home';
import Offboarding from './pages/Offboarding';
import EslSolum from './pages/EslSolum';
import AccessDenied from './components/Shared/AccessDenied';
import ProtectedRoute from './components/Shared/ProtectedRoute';
import OdooImages from './pages/OdooImages';
import Spinner from './components/Shared/Spinner';

const msalInstance = new PublicClientApplication(msalConfig);

const AuthorizedApp: React.FC = () => {
  const { inProgress } = useMsal();
  const { isLoading, hasAnyAccess } = usePermissions();

  if (isLoading || inProgress !== InteractionStatus.None) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ms-gray-50">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="text-sm text-ms-gray-500 mt-3">Vérification des accès...</p>
        </div>
      </div>
    );
  }

  if (!hasAnyAccess) {
    return <AccessDenied />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<MainLayout />}>
          <Route path="/" element={<Home />} />
          <Route
            path="/offboarding"
            element={
              <ProtectedRoute module="offboarding">
                <Offboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/esl-solum"
            element={
              <ProtectedRoute module="eslSolum">
                <EslSolum />
              </ProtectedRoute>
            }
          />
          <Route
            path="/odoo-images"
            element={
              <ProtectedRoute module="odooImages">
                <OdooImages />
              </ProtectedRoute>
            }
          />
          <Route path="/access-denied" element={<AccessDenied moduleLevel />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const LoginPage: React.FC = () => {
  const { instance } = useMsal();

  return (
    <div className="min-h-screen bg-ms-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-sm border border-ms-gray-200 p-8 max-w-sm text-center">
        <div className="w-16 h-16 bg-ms-blue rounded-lg flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-ms-gray-800 mb-2">IT Portal</h1>
        <p className="text-sm text-ms-gray-500 mb-6">
          Connectez-vous avec votre compte Microsoft pour accéder au portail.
        </p>
        <button
          onClick={() => instance.loginRedirect(loginRequest)}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-ms-blue text-white rounded-md hover:bg-ms-blue-dark transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4" viewBox="0 0 21 21" fill="none">
            <rect width="10" height="10" fill="#f25022" />
            <rect x="11" width="10" height="10" fill="#7fba00" />
            <rect y="11" width="10" height="10" fill="#00a4ef" />
            <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
          </svg>
          Se connecter avec Microsoft
        </button>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <PermissionsProvider>
          <AuthorizedApp />
        </PermissionsProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
};

export default App;
